"use client"

import * as React from "react"
import { Calendar } from "@/components/ui/calendar"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { format } from "date-fns"
import { th } from "date-fns/locale"
import type { DateRange } from "react-day-picker"

interface CustomDaterangeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm?: (dateRange: DateRange | undefined) => void
}

export function CustomDateRangeDialog({
  open,
  onOpenChange,
  onConfirm,
}: CustomDaterangeDialogProps) {
  const [dateRange, setDateRange] = React.useState<DateRange | undefined>(undefined)

  // Reset date range when dialog opens
  React.useEffect(() => {
    if (!open) {
      setDateRange(undefined)
    }
  }, [open])

  const handleConfirm = () => {
    onConfirm?.(dateRange)
    onOpenChange(false)
  }

  // Format date display
  const formatDateDisplay = (date: Date | undefined) => {
    if (!date) return "-"
    return format(date, "dd MMM yyyy", { locale: th })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[680px] p-6 bg-white">
        <DialogHeader className="relative">
          <DialogTitle className="text-2xl font-semibold text-gray-900">
            กำหนดช่วงวันที่
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4">
          {/* Selected Date Range Display */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-1">วันเริ่มต้น</p>
                <p className="text-base font-semibold text-gray-900">
                  {formatDateDisplay(dateRange?.from)}
                </p>
              </div>
              <div className="text-gray-400">→</div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-1">วันสุดท้าย</p>
                <p className="text-base font-semibold text-gray-900">
                  {formatDateDisplay(dateRange?.to)}
                </p>
              </div>
            </div>
            {dateRange?.from && dateRange?.to && (
              <p className="text-xs text-blue-600 mt-2">
                ✓ เลือกแล้ว: {format(dateRange.from, "dd/MM/yyyy")} - {format(dateRange.to, "dd/MM/yyyy")}
              </p>
            )}
          </div>

          {/* Calendar Section */}
          <div className="flex justify-center gap-4">
            <Calendar
              mode="range"
              defaultMonth={dateRange?.from || new Date()}
              selected={dateRange}
              onSelect={setDateRange}
              numberOfMonths={2}
              className="shadow-sm border rounded-lg p-4"
              classNames={{
                range_start: "bg-blue-600 text-white hover:bg-blue-700 rounded-l-md",
                range_end: "bg-blue-600 text-white hover:bg-blue-700 rounded-r-md",
                range_middle: "bg-blue-100 text-blue-900",
                today: "bg-orange-100 text-orange-900 font-bold border-2 border-orange-400",
              }}
            />
          </div>

          <div className="flex justify-between items-end gap-4 mt-6">
            {/* Info Text */}
            <div className="grow">
              <p className="text-sm text-gray-600">
                รายงานจะเริ่มนับตั้งแต่เวลา 00:00:00 - 23:59:59 ของแต่ละวัน
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              {dateRange?.from && (
                <Button
                  onClick={() => setDateRange(undefined)}
                  variant="outline"
                  className="border-gray-300"
                >
                  ล้างข้อมูล
                </Button>
              )}
              <Button
                onClick={handleConfirm}
                disabled={!dateRange?.from || !dateRange?.to}
                className="bg-brand-primary hover:bg-brand-primary/90 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ยืนยัน
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
